'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getConsentFromCookie, setConsentCookie } from './consent-storage';
import {
   CONSENT_ACCEPTED,
   CONSENT_PENDING,
   CONSENT_REJECTED,
   type ConsentDecision,
} from './consent-types';
import type { ConsentType, Consents } from './use-piwik-consent';
import { trackCustomGAEvent } from '@ifixit/analytics';

interface PpmsType {
   cm: {
      api: {
         (
            command: 'getNewComplianceTypes',
            onFulfilled: (types: ConsentType[]) => void,
            onRejected?: (error: unknown) => void
         ): void;
         (
            command: 'setInitialComplianceSettings',
            settings: { consents: ConsentType[] } | ConsentType[],
            onFulfilled: () => void,
            onRejected?: (error: unknown) => void
         ): void;
         (
            command: 'getComplianceSettings',
            onFulfilled: (complianceSettings: { consents: Consents }) => void,
            onRejected?: (error: unknown) => void
         ): void;
         (
            command: 'setComplianceSettings',
            settings: { consents: Consents },
            onFulfilled: () => void,
            onRejected?: (error: unknown) => void
         ): void;
         (command: 'trackAgreeToAllClick'): void;
         (command: 'trackRejectAllClick'): void;
         (command: 'trackMainFormView'): void;
      };
   };
}

declare global {
   interface Window {
      ppms?: PpmsType;
   }
}

export function useConsentCookie() {
   const [isReady, setIsReady] = useState(false);
   const [consentDecision, setConsentDecision] = useState<ConsentDecision>('pending');

   useEffect(() => {
      let cancelled = false;
      async function initialize() {
         await initializePiwikConsentSettings();

         const consent = getConsentFromCookie();
         if (consent) {
            setConsentDecision(
               consent.isConsented === CONSENT_ACCEPTED
                  ? 'accepted'
                  : consent.isConsented === CONSENT_REJECTED
                    ? 'rejected'
                    : 'pending'
            );
         } else {
            const piwikStatus = await getPiwikConsentStatus();
            if (piwikStatus === CONSENT_ACCEPTED) {
               setConsentDecision('accepted');
               setConsentCookie(CONSENT_ACCEPTED);
            } else if (piwikStatus === CONSENT_REJECTED) {
               setConsentDecision('rejected');
               setConsentCookie(CONSENT_REJECTED);
            } else {
               if (cancelled) {
                  return;
               }
               // The consent banner is being shown to the user for (likely) the first time, mark impression
               trackCustomGAEvent({
                  eventAction: 'Consent Banner Impression',
                  eventCategory: 'Consent',
               });
               setConsentDecision('pending');
               setConsentCookie(CONSENT_PENDING);
            }
         }
         if (cancelled) {
            return;
         }
         setIsReady(true);
      }
      void initialize();
      return () => {
         cancelled = true;
      };
   }, []);

   const acceptAll = useCallback(async () => {
      setConsentCookie(CONSENT_ACCEPTED);
      setConsentDecision('accepted');
      // TODO: Remove syncToPiwik after Piwik stops accepting data (~Feb 2026)
      await syncToPiwik(CONSENT_ACCEPTED);
   }, []);

   const rejectAll = useCallback(async () => {
      setConsentCookie(CONSENT_REJECTED);
      setConsentDecision('rejected');
      // TODO: Remove syncToPiwik after Piwik stops accepting data (~Feb 2026)
      await syncToPiwik(CONSENT_REJECTED);
   }, []);

   return useMemo(
      () => ({ isReady, consentDecision, acceptAll, rejectAll }),
      [isReady, consentDecision, acceptAll, rejectAll]
   );
}

function initializePiwikConsentSettings(): Promise<void> {
   return new Promise(resolve => {
      const ppms = piwik();
      if (!ppms) {
         resolve();
         return;
      }
      ppms.cm.api('getNewComplianceTypes', (newConsentTypes: ConsentType[]) => {
         if (newConsentTypes.length === 0) {
            resolve();
            return;
         }
         ppms.cm.api('setInitialComplianceSettings', { consents: newConsentTypes }, resolve);
      });
   });
}

function getPiwikConsentStatus(): Promise<number | null> {
   return new Promise(resolve => {
      const ppms = piwik();
      if (!ppms) {
         resolve(null);
         return;
      }
      ppms.cm.api('getComplianceSettings', (complianceSettings: { consents: Consents }) => {
         const savedConsents = complianceSettings.consents;
         for (const consentType in savedConsents) {
            const consent = savedConsents[consentType as ConsentType];
            if (consent.status === CONSENT_PENDING) {
               resolve(CONSENT_PENDING);
               return;
            }
         }
         for (const consentType in savedConsents) {
            const consent = savedConsents[consentType as ConsentType];
            if (consent.status === CONSENT_REJECTED) {
               resolve(CONSENT_REJECTED);
               return;
            }
         }
         resolve(CONSENT_ACCEPTED);
      });
   });
}

function syncToPiwik(status: number): Promise<void> {
   return new Promise(resolve => {
      const ppms = piwik();
      if (!ppms) {
         resolve();
         return;
      }
      ppms.cm.api('getComplianceSettings', (complianceSettings: { consents: Consents }) => {
         const consents = complianceSettings.consents;
         for (const consentType in consents) {
            consents[consentType as ConsentType].status = status;
         }
         ppms.cm.api('setComplianceSettings', { consents }, resolve);
      });
   });
}

function piwik(): PpmsType | null {
   if (typeof window === 'undefined') return null;
   return window?.ppms?.cm?.api ? window.ppms : null;
}
